/** Display helpers for lead-acquisition cohort ranking rows (sales, sources, countries). */

import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Link } from '@mui/material';
import {
  leadCohortLeadsUrl,
  leadCohortEnrolledUrl,
  leadActivityWinsUrl,
  leadSessionTypeUrl,
  leadSessionTypeEnrolledUrl,
} from './leadListDeepLinks';

function StatLink({ to, children }) {
  if (!to) return <>{children}</>;
  return (
    <Link
      component={RouterLink}
      to={to}
      underline="hover"
      color="primary"
      onClick={(e) => e.stopPropagation()}
      sx={{ fontWeight: 500 }}
    >
      {children}
    </Link>
  );
}

export function leadCohortLine1(row, { period, dimension } = {}) {
  const leads = row.leads ?? 0;
  const enrolled = row.enrollmentsFromPeriodLeads ?? 0;
  if (!period || !dimension) {
    return `${leads} Leads · ${enrolled} Enrolled from these leads`;
  }
  return (
    <>
      <StatLink to={leadCohortLeadsUrl(period, dimension)}>{leads} Leads</StatLink>
      {' · '}
      <StatLink to={leadCohortEnrolledUrl(period, dimension)}>
        {enrolled} Enrolled from these leads
      </StatLink>
    </>
  );
}

export function leadCohortLine2(row) {
  return `Conversion: ${row.conversionCohortPct ?? row.conversionPct ?? 0}%`;
}

/** Demo vs Trial attended + enrolled among period-acquired leads. */
export function leadCohortDemoTrialLine(row, { period, dimension } = {}) {
  const demoLeads = row.demoLeads ?? 0;
  const demoEnrolled = row.demoEnrolled ?? 0;
  const trialLeads = row.trialLeads ?? 0;
  const trialEnrolled = row.trialEnrolled ?? 0;
  if (!period || !dimension) {
    return `Demo: ${demoLeads} · ${demoEnrolled} enrolled · Trial: ${trialLeads} · ${trialEnrolled} enrolled`;
  }
  return (
    <>
      Demo:{' '}
      <StatLink to={leadSessionTypeUrl(period, dimension, 'demo')}>{demoLeads}</StatLink>
      {' · '}
      <StatLink to={leadSessionTypeEnrolledUrl(period, dimension, 'demo')}>
        {demoEnrolled} enrolled
      </StatLink>
      {' · Trial: '}
      <StatLink to={leadSessionTypeUrl(period, dimension, 'trial')}>{trialLeads}</StatLink>
      {' · '}
      <StatLink to={leadSessionTypeEnrolledUrl(period, dimension, 'trial')}>
        {trialEnrolled} enrolled
      </StatLink>
    </>
  );
}

export function leadCohortActivityLabel(row, { period, dimension } = {}) {
  const all = row.enrollmentsAllPeriod ?? row.enrollments ?? 0;
  const pct = row.conversionActivityPct ?? 0;
  if (!period || !dimension) {
    return `All wins this period: ${all} · ${pct}%`;
  }
  return (
    <>
      All wins this period:{' '}
      <StatLink to={leadActivityWinsUrl(period, dimension)}>{all}</StatLink>
      {` · ${pct}%`}
    </>
  );
}

export function pulseLeadCohortSubtitle(pulse) {
  const block = pulse?.leadToEnrollment;
  if (!block) return '';
  return `${block.leadsCreated ?? 0} Leads · ${block.enrollments ?? 0} Enrolled from these leads`;
}

export function pulseActivityLabel(pulse) {
  const block = pulse?.leadToEnrollment;
  if (!block) return '';
  return `All wins this period: ${block.enrollmentsAllPeriod ?? 0} · ${block.activityPct ?? 0}%`;
}
